import React, { useEffect, useMemo } from "react";
import { canMutateSubscriptionExtras } from "@/utils/hotelSubscriptionAccess";
import { LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SubscriptionPlan } from "@/types/subscription-plan";
import {
  useHotelSubscription,
  useSubscriptionPaymentUrl,
} from "@/components/pages/hotels/hooks/useHotelSubscriptions";
import { HotelSubscriptionStatus } from "@/types/hotel-subscription";

interface HotelSubscriptionPlanCellProps {
  hotelId: string;
  plans: SubscriptionPlan[];
  canManage?: boolean;
  tenantId?: string;
  /** Shown instead of the "No plan" badge (tenant Hotels tab: "—", DV-168 AC-2). */
  emptyPlaceholder?: string;
}

const PAYMENT_POLLING_STORAGE_KEY =
  "hotel-subscription-payment-processing-hotel-id";
const PAYMENT_POLLING_STARTED_AT_STORAGE_KEY =
  "hotel-subscription-payment-processing-started-at";
const PAYMENT_POLLING_INTERVAL_MS = 3000;
const PAYMENT_POLLING_TIMEOUT_MS = 30000;

function getBillingSubscriptionsReturnUrl(): string {
  const url = new URL("/billing-subscriptions", window.location.origin);
  return url.toString();
}

function getStatusVariant(status?: string) {
  switch (status) {
    case "ACTIVE":
    case "TRIALING":
      return "status_done";
    case "INCOMPLETE":
    case "PAST_DUE":
    case "UNPAID":
      return "status_canceled";
    case "CANCELED":
    case "INCOMPLETE_EXPIRED":
    case "PAUSED":
      return "status_canceled";
    default:
      return "new_secondary";
  }
}

function getStatusLabel(
  status: HotelSubscriptionStatus | string,
  t: (key: string) => string,
): string {
  const key = `hotels.subscriptions.status.${status}`;
  const translated = t(key);

  return translated === key ? status : translated;
}

function requiresPayment(status?: string): boolean {
  return (
    status === "INCOMPLETE" || status === "PAST_DUE" || status === "UNPAID"
  );
}

export const HotelSubscriptionPlanCell: React.FC<
  HotelSubscriptionPlanCellProps
> = ({ hotelId, plans, canManage = true, tenantId, emptyPlaceholder }) => {
  const { t } = useTranslation();
  const shouldPoll = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }

    const pollingHotelId = window.sessionStorage.getItem(
      PAYMENT_POLLING_STORAGE_KEY,
    );
    const startedAt = window.sessionStorage.getItem(
      PAYMENT_POLLING_STARTED_AT_STORAGE_KEY,
    );
    const startedAtMs = Number(startedAt);
    const withinPollingWindow =
      Number.isFinite(startedAtMs) &&
      Date.now() - startedAtMs < PAYMENT_POLLING_TIMEOUT_MS;

    return pollingHotelId === hotelId && withinPollingWindow;
  }, [hotelId]);
  const { data: subscription, isLoading } = useHotelSubscription(
    hotelId,
    true,
    {
      refetchInterval: shouldPoll ? PAYMENT_POLLING_INTERVAL_MS : false,
    },
    tenantId,
  );
  const { mutateAsync: getPaymentUrl, isPending: isPaymentUrlPending } =
    useSubscriptionPaymentUrl();

  useEffect(() => {
    if (typeof window === "undefined" || !shouldPoll) {
      return;
    }

    if (!subscription || !requiresPayment(subscription.status)) {
      window.sessionStorage.removeItem(PAYMENT_POLLING_STORAGE_KEY);
      window.sessionStorage.removeItem(PAYMENT_POLLING_STARTED_AT_STORAGE_KEY);
    }
  }, [shouldPoll, subscription]);

  const handlePayNow = async () => {
    if (!canManage) {
      return;
    }

    if (!subscription?.tenantSubscriptionId) {
      toast.error(t("hotels.subscriptions.paymentUrlError"));
      return;
    }

    try {
      const response = await getPaymentUrl({
        subscriptionId: subscription.tenantSubscriptionId,
        returnUrl: getBillingSubscriptionsReturnUrl(),
      });

      if (!response.url) {
        toast.error(t("hotels.subscriptions.paymentUrlUnavailable"));
        return;
      }

      window.sessionStorage.setItem(PAYMENT_POLLING_STORAGE_KEY, hotelId);
      window.sessionStorage.setItem(
        PAYMENT_POLLING_STARTED_AT_STORAGE_KEY,
        String(Date.now()),
      );
      window.location.assign(response.url);
    } catch {
      toast.error(t("hotels.subscriptions.paymentUrlError"));
    }
  };

  if (isLoading) {
    return (
      <span className="text-muted-foreground inline-flex items-center justify-center">
        <LoaderCircle className="size-4 animate-spin" />
      </span>
    );
  }

  if (!subscription && emptyPlaceholder !== undefined) {
    return <span>{emptyPlaceholder}</span>;
  }

  if (!subscription) {
    return (
      <Badge variant="new_secondary">{t("hotels.subscriptions.noPlan")}</Badge>
    );
  }

  const plan = plans.find((item) => item.id === subscription.planId);
  const paymentRequired = requiresPayment(subscription.status);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Badge variant="new_default">
          {subscription.plan?.title ??
            plan?.title ??
            t("hotels.subscriptions.unknownPlan")}
        </Badge>
        <Badge variant={getStatusVariant(subscription.status)}>
          {getStatusLabel(subscription.status, t)}
        </Badge>
      </div>
      {paymentRequired && canManage && canMutateSubscriptionExtras(tenantId) ? (
        <Button
          type="button"
          variant="new_default"
          size="sm"
          disabled={isPaymentUrlPending}
          onClick={() => void handlePayNow()}
        >
          {isPaymentUrlPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : null}
          {t("hotels.subscriptions.payNow")}
        </Button>
      ) : null}
    </div>
  );
};
